import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Tabs,
  Table,
  Card,
  Button,
  Space,
  message,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Tag,
  Popconfirm,
  Switch,
  DatePicker,
  Row,
  Col,
  Typography,
  Tooltip,
  Drawer,
  Statistic,
  Pagination,
  Alert,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  InfoCircleOutlined,
  PauseCircleOutlined,
  ThunderboltOutlined,
  ReloadOutlined,
  SyncOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { broadcastAPI, channelAPI, botAPI } from '../services/api';
import InlineButtonEditor, { inlineButtonsRule } from '../components/InlineButtonEditor';
import MediaUploader from '../components/MediaUploader';
import DynamicVariableEditor, { DynamicVariableConfig } from '../components/DynamicVariableEditor';
import CustomEmojiPicker from '../components/CustomEmojiPicker';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;
const { TabPane } = Tabs;

// ==================== 类型定义 ====================
interface BroadcastTask {
  id: number;
  bot_id: number;
  target_id: string | null;
  target_type: 'group' | 'channel' | 'private' | 'all_private';
  title: string;
  content: string;
  parse_mode: string;
  media_type: string | null;
  media_url: string | null;
  inline_buttons: any[][] | null;
  dynamic_variables: DynamicVariableConfig | null;
  schedule_type: 'once' | 'interval' | 'cron';
  schedule_at: string | null;
  interval_minutes: number | null;
  cron_expr: string | null;
  pin_message: number;
  pin_duration: number | null;
  silent: number;
  protect_content: number;
  last_sent_at: string | null;
  next_send_at: string | null;
  send_count: number;
  status: number;
  created_at: string;
}

interface SendResult {
  id: number;
  task_id: number;
  batch_no: number;
  user_id: number;
  username: string | null;
  status: number; // 0=待发 1=成功 2=失败
  error_code: string | null;
  error_msg: string | null;
  sent_at: string | null;
  created_at: string;
}

interface SendBatch {
  batch_no: number;
  total: number;
  success: number;
  failed: number;
  started_at: string;
}

interface ManagedChannel {
  id: number;
  bot_id: number;
  channel_id: string;
  channel_username: string | null;
  title: string;
  invite_link: string | null;
  member_count: number | null;
  status: number;
  created_at: string;
}

interface BotConfig {
  id: number;
  bot_name: string;
  bot_username: string;
}

// ==================== 频道管理子页 ====================
const ChannelsTab: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [channels, setChannels] = useState<ManagedChannel[]>([]);
  const [bots, setBots] = useState<BotConfig[]>([]);
  const [selectedBotId, setSelectedBotId] = useState<number | undefined>();
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();

  const loadBots = async () => {
    try {
      const res = await botAPI.getAll();
      setBots(res.data || []);
    } catch (_) {}
  };

  const loadChannels = async () => {
    setLoading(true);
    try {
      const res = await channelAPI.getAll(selectedBotId);
      setChannels(res.data || []);
    } catch (err: any) {
      message.error('加载频道列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadBots(); }, []);
  useEffect(() => { loadChannels(); }, [selectedBotId]);

  const handleAdd = async () => {
    try {
      const values = await form.validateFields();
      await channelAPI.create({
        botId: values.botId,
        channelId: values.channelId,
        channelUsername: values.channelUsername || undefined,
        title: values.title || undefined,
      });
      message.success('频道添加成功');
      setModalVisible(false);
      form.resetFields();
      loadChannels();
    } catch (err: any) {
      if (err?.response?.data?.error) {
        message.error(err.response.data.error);
      }
    }
  };

  const handleRefresh = async (id: number) => {
    try {
      const res = await channelAPI.refresh(id);
      message.success(`刷新成功: ${res.data.title} (${res.data.memberCount} 成员)`);
      loadChannels();
    } catch (err: any) {
      message.error(err?.response?.data?.error || '刷新失败');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await channelAPI.delete(id);
      message.success('删除成功');
      loadChannels();
    } catch (err: any) {
      message.error('删除失败');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '频道标题', dataIndex: 'title' },
    {
      title: '频道 ID',
      dataIndex: 'channel_id',
      render: (v: string, r: ManagedChannel) => (
        <Space direction="vertical" size={0}>
          <Text code>{v}</Text>
          {r.channel_username && <Text type="secondary">@{r.channel_username}</Text>}
        </Space>
      ),
    },
    {
      title: '成员数',
      dataIndex: 'member_count',
      render: (v: number | null) => (v !== null ? v.toLocaleString() : '-'),
    },
    {
      title: '状态',
      dataIndex: 'status',
      render: (v: number) => (
        <Tag color={v === 1 ? 'green' : 'default'}>{v === 1 ? '正常' : '停用'}</Tag>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      render: (_: any, record: ManagedChannel) => (
        <Space>
          <Tooltip title="从Telegram刷新信息">
            <Button size="small" icon={<SyncOutlined />} onClick={() => handleRefresh(record.id)} />
          </Tooltip>
          <Popconfirm title="确认删除此频道？" onConfirm={() => handleDelete(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Select
          placeholder="筛选机器人"
          allowClear
          style={{ width: 200 }}
          onChange={(v) => setSelectedBotId(v)}
          value={selectedBotId}
        >
          {bots.map((b) => (
            <Option key={b.id} value={b.id}>{b.bot_name}</Option>
          ))}
        </Select>
        <Button icon={<ReloadOutlined />} onClick={loadChannels} loading={loading}>刷新</Button>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalVisible(true)}>添加频道</Button>
      </Space>

      <Table rowKey="id" loading={loading} dataSource={channels} columns={columns} size="small" />

      <Modal
        title="添加频道"
        open={modalVisible}
        onOk={handleAdd}
        onCancel={() => { setModalVisible(false); form.resetFields(); }}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item label="机器人" name="botId" rules={[{ required: true, message: '请选择机器人' }]}>
            <Select placeholder="选择机器人">
              {bots.map((b) => (
                <Option key={b.id} value={b.id}>{b.bot_name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="频道 ID" name="channelId" rules={[{ required: true, message: '请输入频道ID' }]}>
            <Input placeholder="-1001234567890" />
          </Form.Item>
          <Form.Item label="频道用户名（可选）" name="channelUsername">
            <Input placeholder="my_channel（不含@）" />
          </Form.Item>
          <Form.Item label="备注标题（可选）" name="title">
            <Input placeholder="如未填写将从Telegram自动获取" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

// ==================== 广播任务子页 ====================
const SCHEDULE_TYPE_LABELS: Record<string, string> = {
  once: '一次性',
  interval: '间隔重复',
  cron: 'Cron 表达式',
};

const TARGET_TYPE_LABELS: Record<string, { label: string; color: string }> = {
  group: { label: '群组', color: 'blue' },
  channel: { label: '频道', color: 'cyan' },
  private: { label: '私聊', color: 'geekblue' },
  all_private: { label: '全量私聊', color: 'purple' },
};

const BroadcastTab: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState<BroadcastTask[]>([]);
  const [bots, setBots] = useState<BotConfig[]>([]);
  const [selectedBotId, setSelectedBotId] = useState<number | undefined>();
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTask, setEditingTask] = useState<BroadcastTask | null>(null);
  const [form] = Form.useForm();
  const contentTextRef = useRef<any>(null);
  const scheduleType = Form.useWatch('scheduleType', form);
  const mediaType = Form.useWatch('mediaType', form);
  const targetType = Form.useWatch('targetType', form);
  const formBotId = Form.useWatch('botId', form);

  const handleEmojiInsert = useCallback((htmlTag: string) => {
    const textarea = contentTextRef.current?.resizableTextArea?.textArea as HTMLTextAreaElement | undefined;
    const currentVal = form.getFieldValue('content') || '';
    if (textarea) {
      const start = textarea.selectionStart ?? currentVal.length;
      const end = textarea.selectionEnd ?? currentVal.length;
      const newVal = currentVal.slice(0, start) + htmlTag + currentVal.slice(end);
      form.setFieldsValue({ content: newVal });
      setTimeout(() => {
        textarea.focus();
        const pos = start + htmlTag.length;
        textarea.setSelectionRange(pos, pos);
      }, 0);
    } else {
      form.setFieldsValue({ content: currentVal + htmlTag });
    }
  }, [form]);

  // 从动态变量配置中提取字段名列表（用于 InlineButtonEditor 的变量提示）
  const getDynamicVarFields = (dvConfig: DynamicVariableConfig | null): string[] => {
    if (!dvConfig) return [];
    const requests = dvConfig.requests || [];
    return requests
      .flatMap((r: any) => r.response_fields || [])
      .filter((f: string) => f.trim());
  };

  const getAuthCodeVarNames = (dvConfig: DynamicVariableConfig | null): string[] => {
    if (!dvConfig) return [];
    return (dvConfig.auth_codes || [])
      .map((c: any) => c.variable_name)
      .filter(Boolean);
  };

  // 私聊用户预估数
  const [privateUsersCount, setPrivateUsersCount] = useState<number | null>(null);
  const [countLoading, setCountLoading] = useState(false);

  // 发送结果 Drawer
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerTask, setDrawerTask] = useState<BroadcastTask | null>(null);
  const [batches, setBatches] = useState<SendBatch[]>([]);
  const [currentBatchNo, setCurrentBatchNo] = useState<number>(0);
  const [results, setResults] = useState<SendResult[]>([]);
  const [resultTotal, setResultTotal] = useState(0);
  const [resultPage, setResultPage] = useState(1);
  const [resultLoading, setResultLoading] = useState(false);

  const loadBots = async () => {
    try {
      const res = await botAPI.getAll();
      setBots(res.data || []);
    } catch (_) {}
  };

  const loadTasks = async () => {
    setLoading(true);
    try {
      const res = await broadcastAPI.getAll({ botId: selectedBotId });
      setTasks(res.data || []);
    } catch (err: any) {
      message.error('加载广播任务失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadBots(); }, []);
  useEffect(() => { loadTasks(); }, [selectedBotId]);

  // 当切换为 all_private 且已选机器人时，获取预估人数
  useEffect(() => {
    if (targetType === 'all_private' && formBotId) {
      setCountLoading(true);
      broadcastAPI.getPrivateUsersCount(formBotId)
        .then(res => setPrivateUsersCount(res.data.count))
        .catch(() => setPrivateUsersCount(null))
        .finally(() => setCountLoading(false));
    } else {
      setPrivateUsersCount(null);
    }
  }, [targetType, formBotId]);

  // 加载发送结果列表
  const loadResults = useCallback(async (taskId: number, batchNo: number, page = 1) => {
    setResultLoading(true);
    try {
      const res = await broadcastAPI.getSendResults(taskId, { batchNo, page, pageSize: 50 });
      setResults(res.data.data || []);
      setResultTotal(res.data.total || 0);
      setResultPage(page);
    } catch (_) {
      message.error('加载发送结果失败');
    } finally {
      setResultLoading(false);
    }
  }, []);

  // 打开发送结果 Drawer
  const openResultDrawer = async (task: BroadcastTask) => {
    setDrawerTask(task);
    setDrawerVisible(true);
    setResults([]);
    setBatches([]);
    setCurrentBatchNo(0);
    try {
      const res = await broadcastAPI.getSendBatches(task.id);
      const batchList: SendBatch[] = res.data || [];
      setBatches(batchList);
      if (batchList.length > 0) {
        const latest = batchList[0].batch_no;
        setCurrentBatchNo(latest);
        await loadResults(task.id, latest, 1);
      }
    } catch (_) {
      message.error('加载批次列表失败');
    }
  };

  const openModal = (task?: BroadcastTask) => {
    setEditingTask(task || null);
    if (task) {
      form.setFieldsValue({
        botId: task.bot_id,
        targetId: task.target_id,
        targetType: task.target_type,
        title: task.title,
        content: task.content,
        parseMode: task.parse_mode,
        mediaType: task.media_type,
        mediaUrl: task.media_url,
        inlineButtons: Array.isArray(task.inline_buttons) ? task.inline_buttons : [],
        dynamicVariables: task.dynamic_variables || null,
        scheduleType: task.schedule_type,
        scheduleAt: task.schedule_at ? dayjs(task.schedule_at) : null,
        intervalMinutes: task.interval_minutes,
        cronExpr: task.cron_expr,
        pinMessage: task.pin_message === 1,
        pinDuration: task.pin_duration,
        silent: task.silent === 1,
        protectContent: task.protect_content === 1,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        scheduleType: 'once',
        targetType: 'group',
        parseMode: 'HTML',
        pinMessage: false,
        silent: false,
        protectContent: true,
        inlineButtons: [],
      });
    }
    setModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const inlineButtons =
        Array.isArray(values.inlineButtons) && values.inlineButtons.length > 0
          ? values.inlineButtons
          : null;

      const resolvedTargetType = values.targetType || 'group';
      const data = {
        botId: values.botId,
        targetId: resolvedTargetType === 'all_private' ? null : (values.targetId || null),
        targetType: resolvedTargetType,
        title: values.title || '',
        content: values.content,
        parseMode: values.parseMode || 'HTML',
        mediaType: values.mediaType || null,
        mediaUrl: values.mediaUrl || null,
        inlineButtons,
        dynamicVariables: values.dynamicVariables || null,
        scheduleType: values.scheduleType,
        scheduleAt: values.scheduleAt ? values.scheduleAt.toISOString() : null,
        intervalMinutes: values.intervalMinutes || null,
        cronExpr: values.cronExpr || null,
        pinMessage: !!values.pinMessage,
        pinDuration: values.pinDuration || null,
        silent: !!values.silent,
        protectContent: !!values.protectContent,
      };

      if (editingTask) {
        await broadcastAPI.update(editingTask.id, data);
        message.success('更新成功');
      } else {
        await broadcastAPI.create(data);
        message.success('创建成功');
      }
      setModalVisible(false);
      loadTasks();
    } catch (err: any) {
      if (err?.response?.data?.error) {
        message.error(err.response.data.error);
      }
    }
  };

  const handleExecute = async (task: BroadcastTask) => {
    try {
      const res = await broadcastAPI.execute(task.id);
      const result = res.data?.result;
      if (task.target_type === 'all_private' && result?.total !== undefined) {
        message.success(`执行完成：${result.successCount}/${result.total} 成功，${result.failCount} 失败`);
        loadTasks();
        // 自动打开结果 Drawer
        await openResultDrawer({ ...task, send_count: (task.send_count || 0) + 1 });
      } else {
        message.success('已立即执行');
        loadTasks();
      }
    } catch (err: any) {
      message.error(err?.response?.data?.error || '执行失败');
    }
  };

  const handleToggle = async (task: BroadcastTask) => {
    try {
      if (task.status === 1) {
        await broadcastAPI.pause(task.id);
        message.success('已暂停');
      } else {
        await broadcastAPI.resume(task.id);
        message.success('已恢复');
      }
      loadTasks();
    } catch (err: any) {
      message.error('操作失败');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await broadcastAPI.delete(id);
      message.success('删除成功');
      loadTasks();
    } catch (_) {
      message.error('删除失败');
    }
  };

  // 当前批次的统计数据
  const currentBatch = batches.find(b => b.batch_no === currentBatchNo);

  const resultColumns = [
    {
      title: '用户 ID',
      dataIndex: 'user_id',
      width: 120,
      render: (v: number) => <Text code>{v}</Text>,
    },
    {
      title: '用户名',
      dataIndex: 'username',
      render: (v: string | null) => v ? <Text>@{v}</Text> : <Text type="secondary">-</Text>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 80,
      render: (v: number) => {
        if (v === 1) return <Tag color="success">成功</Tag>;
        if (v === 2) return <Tag color="error">失败</Tag>;
        return <Tag>待发</Tag>;
      },
    },
    {
      title: '错误码',
      dataIndex: 'error_code',
      width: 80,
      render: (v: string | null) => v ? <Tag color="warning">{v}</Tag> : '-',
    },
    {
      title: '错误信息',
      dataIndex: 'error_msg',
      ellipsis: true,
      render: (v: string | null) =>
        v ? (
          <Tooltip title={v}>
            <Text type="danger" style={{ fontSize: 12 }}>{v.slice(0, 40)}{v.length > 40 ? '...' : ''}</Text>
          </Tooltip>
        ) : '-',
    },
    {
      title: '发送时间',
      dataIndex: 'sent_at',
      width: 160,
      render: (v: string | null) => v ? <Text style={{ fontSize: 12 }}>{v}</Text> : '-',
    },
  ];

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: '标题',
      dataIndex: 'title',
      render: (v: string, r: BroadcastTask) => {
        const tt = TARGET_TYPE_LABELS[r.target_type];
        return (
          <Space direction="vertical" size={0}>
            <Text strong>{v || '(无标题)'}</Text>
            <Space size={4}>
              {tt && <Tag color={tt.color} style={{ fontSize: 11, padding: '0 4px' }}>{tt.label}</Tag>}
              {r.target_type !== 'all_private' && r.target_id && (
                <Text type="secondary" style={{ fontSize: 12 }}>{r.target_id}</Text>
              )}
            </Space>
          </Space>
        );
      },
    },
    {
      title: '计划类型',
      dataIndex: 'schedule_type',
      render: (v: string, r: BroadcastTask) => (
        <Space direction="vertical" size={0}>
          <Tag color="blue">{SCHEDULE_TYPE_LABELS[v] || v}</Tag>
          {v === 'interval' && r.interval_minutes && (
            <Text type="secondary" style={{ fontSize: 12 }}>每 {r.interval_minutes} 分钟</Text>
          )}
          {v === 'cron' && r.cron_expr && (
            <Text code style={{ fontSize: 12 }}>{r.cron_expr}</Text>
          )}
          {v === 'once' && r.schedule_at && (
            <Text type="secondary" style={{ fontSize: 12 }}>{dayjs(r.schedule_at).format('YYYY-MM-DD HH:mm:ss')}</Text>
          )}
        </Space>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      render: (v: number) => (
        <Tag color={v === 1 ? 'green' : 'default'}>{v === 1 ? '运行中' : '已暂停'}</Tag>
      ),
    },
    {
      title: '发送统计',
      render: (_: any, r: BroadcastTask) => (
        <Space direction="vertical" size={0}>
          <Text>已发 {r.send_count} 次</Text>
          {r.next_send_at && (
            <Text type="secondary" style={{ fontSize: 12 }}>下次: {dayjs(r.next_send_at).format('YYYY-MM-DD HH:mm:ss')}</Text>
          )}
        </Space>
      ),
    },
    {
      title: '附加',
      render: (_: any, r: BroadcastTask) => (
        <Space>
          {r.pin_message === 1 && <Tag color="gold">置顶</Tag>}
          {r.silent === 1 && <Tag>静默</Tag>}
          {r.protect_content === 1 && <Tag color="red">禁转</Tag>}
          {r.media_type && <Tag color="purple">{r.media_type}</Tag>}
        </Space>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      render: (_: any, record: BroadcastTask) => (
        <Space wrap>
          <Tooltip title="立即执行">
            <Button size="small" icon={<ThunderboltOutlined />} onClick={() => handleExecute(record)} />
          </Tooltip>
          <Tooltip title="发送结果">
            <Button size="small" icon={<BarChartOutlined />} onClick={() => openResultDrawer(record)} />
          </Tooltip>
          <Tooltip title={record.status === 1 ? '暂停' : '恢复'}>
            <Button
              size="small"
              icon={record.status === 1 ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
              onClick={() => handleToggle(record)}
            />
          </Tooltip>
          <Button size="small" onClick={() => openModal(record)}>编辑</Button>
          <Popconfirm title="确认删除此广播任务？" onConfirm={() => handleDelete(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Select
          placeholder="筛选机器人"
          allowClear
          style={{ width: 200 }}
          onChange={(v) => setSelectedBotId(v)}
          value={selectedBotId}
        >
          {bots.map((b) => (
            <Option key={b.id} value={b.id}>{b.bot_name}</Option>
          ))}
        </Select>
        <Button icon={<ReloadOutlined />} onClick={loadTasks} loading={loading}>刷新</Button>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>新建任务</Button>
      </Space>

      <Table rowKey="id" loading={loading} dataSource={tasks} columns={columns} size="small" />

      {/* 新建/编辑弹窗 */}
      <Modal
        title={editingTask ? '编辑广播任务' : '新建广播任务'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => { setModalVisible(false); form.resetFields(); }}
        width={800}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="机器人" name="botId" rules={[{ required: true, message: '请选择机器人' }]}>
                <Select placeholder="选择机器人">
                  {bots.map((b) => (
                    <Option key={b.id} value={b.id}>{b.bot_name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="目标类型" name="targetType">
                <Select>
                  <Option value="group">群组</Option>
                  <Option value="channel">频道</Option>
                  <Option value="private">私聊（单人）</Option>
                  <Option value="all_private">全量私聊联系人</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {targetType === 'all_private' ? (
            <Card size="small" style={{ marginBottom: 16, background: '#f0f5ff', border: '1px solid #adc6ff' }}>
              {countLoading ? (
                <Text type="secondary">正在获取预估人数...</Text>
              ) : privateUsersCount !== null ? (
                <Text>预计发送给 <Text strong style={{ color: '#1677ff' }}>{privateUsersCount}</Text> 位曾私聊过机器人的用户</Text>
              ) : (
                <Text type="secondary">请先选择机器人以获取预估人数</Text>
              )}
            </Card>
          ) : (
            <Form.Item
              label="目标 ID（群组ID / 频道ID / 用户ID）"
              name="targetId"
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (getFieldValue('targetType') === 'all_private' || value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('请输入目标ID'));
                  },
                }),
              ]}
            >
              <Input placeholder="-1001234567890" />
            </Form.Item>
          )}

          <Form.Item label="任务标题（备注）" name="title">
            <Input placeholder="可选，方便识别任务" />
          </Form.Item>

          <Alert
            type="info"
            showIcon
            icon={<InfoCircleOutlined />}
            style={{ marginBottom: 16 }}
            message="内置变量（可在消息内容和按钮 URL 中使用）"
            description={
              <span>
                <Tag>{'{user_id}'}</Tag> <Tag>{'{bot_id}'}</Tag> <Tag>{'{bot_username}'}</Tag> <Tag>{'{bot_name}'}</Tag> <Tag>{'{bot_invite_link}'}</Tag> <Tag color="green">{'{start}'}</Tag>
                <br />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {'{start}'} = 用户 /start 时携带的深度链接参数（如邀请码）。示例：https://game.example.com/?tglogin={'{login_code}'}&invite={'{start}'}
                </Typography.Text>
              </span>
            }
          />

          <Form.Item
            label={
              <Space>
                <span>消息内容</span>
                <CustomEmojiPicker onInsert={handleEmojiInsert} buttonSize="small" />
              </Space>
            }
            name="content"
            rules={[{ required: true, message: '请输入消息内容' }]}
          >
            <TextArea ref={contentTextRef} rows={4} placeholder="支持 HTML/Markdown 格式，可使用 {变量名} 占位符" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="解析模式" name="parseMode">
                <Select>
                  <Option value="HTML">HTML</Option>
                  <Option value="Markdown">Markdown</Option>
                  <Option value="">纯文本</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="媒体类型" name="mediaType">
                <Select allowClear placeholder="无媒体">
                  <Option value="photo">图片</Option>
                  <Option value="video">视频</Option>
                  <Option value="document">文件</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              {mediaType && mediaType !== 'document' && (
                <Form.Item label="媒体文件" name="mediaUrl">
                  <MediaUploader mediaType={mediaType === 'video' ? 'video' : 'photo'} />
                </Form.Item>
              )}
              {mediaType === 'document' && (
                <Form.Item label="媒体 URL / file_id" name="mediaUrl">
                  <Input placeholder="URL 或 file_id" />
                </Form.Item>
              )}
            </Col>
          </Row>

          <Form.Item label="计划类型" name="scheduleType" rules={[{ required: true }]}>
            <Select>
              <Option value="once">一次性</Option>
              <Option value="interval">间隔重复</Option>
              <Option value="cron" disabled>Cron 表达式（暂未支持）</Option>
            </Select>
          </Form.Item>

          {scheduleType === 'once' && (
            <Form.Item label="发送时间" name="scheduleAt" rules={[{ required: true, message: '请选择发送时间' }]}>
              <DatePicker showTime style={{ width: '100%' }} />
            </Form.Item>
          )}
          {scheduleType === 'interval' && (
            <Form.Item label="间隔分钟数" name="intervalMinutes" rules={[{ required: true, message: '请输入间隔分钟数' }]}>
              <InputNumber min={1} style={{ width: '100%' }} placeholder="60" />
            </Form.Item>
          )}
          {scheduleType === 'cron' && (
            <Form.Item
              label="Cron 表达式"
              name="cronExpr"
              rules={[{ required: true, message: '请输入Cron表达式' }]}
              extra="示例: 0 9 * * * (每天9点)"
            >
              <Input placeholder="0 9 * * *" />
            </Form.Item>
          )}

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="置顶消息" name="pinMessage" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="自动取消置顶（分钟）" name="pinDuration">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="0=不取消" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="静默发送" name="silent" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="禁止转发" name="protectContent" valuePropName="checked"
                extra="不可转发/保存/复制">
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          {/* 动态变量（外部API + 登录短码） */}
          <Form.Item label="动态变量（外部API + 登录短码）" name="dynamicVariables">
            <DynamicVariableEditor />
          </Form.Item>

          {/* Inline 按钮 - 传入动态变量和登录短码的变量名提示 */}
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const dvConfig = getFieldValue('dynamicVariables');
              const dvFields = getDynamicVarFields(dvConfig);
              const authCodeNames = getAuthCodeVarNames(dvConfig);
              return (
                <Form.Item label="Inline 按钮" name="inlineButtons" rules={[inlineButtonsRule]}>
                  <InlineButtonEditor
                    dynamicVarFields={dvFields}
                    authCodeVarNames={authCodeNames}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
        </Form>
      </Modal>

      {/* 发送结果监控 Drawer */}
      <Drawer
        title={`发送结果 - ${drawerTask?.title || `任务 #${drawerTask?.id}`}`}
        placement="right"
        width={760}
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
      >
        {drawerTask && (
          <>
            {/* 批次选择 */}
            <Space style={{ marginBottom: 16 }} wrap>
              <Text strong>执行批次：</Text>
              {batches.length === 0 ? (
                <Text type="secondary">暂无发送记录</Text>
              ) : (
                batches.map(b => (
                  <Button
                    key={b.batch_no}
                    size="small"
                    type={b.batch_no === currentBatchNo ? 'primary' : 'default'}
                    onClick={() => {
                      setCurrentBatchNo(b.batch_no);
                      loadResults(drawerTask.id, b.batch_no, 1);
                    }}
                  >
                    第 {b.batch_no} 次
                  </Button>
                ))
              )}
              <Tooltip title="刷新当前批次">
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={() => currentBatchNo && loadResults(drawerTask.id, currentBatchNo, resultPage)}
                />
              </Tooltip>
            </Space>

            {/* 统计卡片 */}
            {currentBatch && (
              <Row gutter={12} style={{ marginBottom: 16 }}>
                <Col span={6}>
                  <Card size="small" style={{ textAlign: 'center' }}>
                    <Statistic title="总计" value={currentBatch.total} />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card size="small" style={{ textAlign: 'center' }}>
                    <Statistic title="成功" value={currentBatch.success} valueStyle={{ color: '#52c41a' }} />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card size="small" style={{ textAlign: 'center' }}>
                    <Statistic title="失败" value={currentBatch.failed} valueStyle={{ color: '#ff4d4f' }} />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card size="small" style={{ textAlign: 'center' }}>
                    <Statistic
                      title="成功率"
                      value={currentBatch.total > 0 ? Math.round(currentBatch.success / currentBatch.total * 100) : 0}
                      suffix="%"
                      valueStyle={{ color: '#1677ff' }}
                    />
                  </Card>
                </Col>
              </Row>
            )}

            {currentBatch && (
              <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
                执行时间：{currentBatch.started_at}
              </Text>
            )}

            {/* 结果列表 */}
            <Table
              rowKey="id"
              loading={resultLoading}
              dataSource={results}
              columns={resultColumns}
              size="small"
              pagination={false}
              scroll={{ x: 600 }}
            />

            {resultTotal > 50 && (
              <div style={{ marginTop: 12, textAlign: 'right' }}>
                <Pagination
                  size="small"
                  current={resultPage}
                  pageSize={50}
                  total={resultTotal}
                  onChange={(page) => loadResults(drawerTask.id, currentBatchNo, page)}
                  showTotal={(total) => `共 ${total} 条`}
                />
              </div>
            )}
          </>
        )}
      </Drawer>
    </>
  );
};

// ==================== 主页 ====================
const BroadcastTasks: React.FC = () => {
  return (
    <div style={{ padding: '0 24px' }}>
      <Tabs defaultActiveKey="broadcast" size="large">
        <TabPane tab="定时广播" key="broadcast">
          <BroadcastTab />
        </TabPane>
        <TabPane tab="频道管理" key="channels">
          <ChannelsTab />
        </TabPane>
      </Tabs>
    </div>
  );
};

export default BroadcastTasks;
